import type { ReactNode } from 'react';
import { Card, CardHeader, CardTitle, CardBody, Chip, Badge } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Boxes } from 'lucide-react';
import { formatMoney, formatDate } from '@/lib/format';
import {
  useServiceConfigoptions,
  useServiceAddonConfigoptions,
  type ServiceConfigOption,
  type ServiceRow,
  type ServiceDetail,
} from '@/api/services';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import type { ConversantTemplateConfig } from './config-template-kind';

/** One Conversant table row: a configoption of the service, or an addon service. */
interface ConversantRow {
  key: string;
  entityName?: string | null;
  metricName: string;
  isAddon?: boolean;
  commitment_value?: number;
  commitment_time?: number;
  commitment_prepaid?: boolean | null;
  commitment_price_per_unit?: number;
  commitment_price_total?: number;
  commitment_startdate?: string | null;
  commitment_enddate?: string | null;
  employee_names?: string[];
  service_start_date?: string | null;
  service_stopped_date?: string | null;
}

const muted = { color: 'var(--vn-fg-muted)' } as const;
const nf = new Intl.NumberFormat('en-US');
const rightHeader = (label: string) => () => <span style={{ display: 'block', textAlign: 'right' }}>{label}</span>;

/** Commitment cell: PAYG when zero, else "1,000 GB" (+ "/ Nmo" for multi-month term). */
function commitmentCell(row: ConversantRow): ReactNode {
  const v = row.commitment_value;
  if (!v) return <span style={{ ...muted, fontSize: 'var(--vn-font-size-xs)' }}>PAYG</span>;
  const suffix = row.commitment_time && row.commitment_time > 1 ? `GB / ${row.commitment_time}mo` : 'GB';
  return (
    <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
      {`${nf.format(v)} ${suffix}`}
    </span>
  );
}

/** Term cell: commitment date range ("DD/MM – DD/MM/YYYY"), hidden when no committed value. */
function termCell(row: ConversantRow): ReactNode {
  if (!row.commitment_value) return null;
  const start = formatDate(row.commitment_startdate);
  const end = formatDate(row.commitment_enddate);
  if (!start && !end) return <span style={muted}>—</span>;
  return (
    <span style={{ display: 'block', textAlign: 'right', whiteSpace: 'nowrap' }}>
      {start} – {end}
    </span>
  );
}

function buildRows(
  detail: ServiceDetail,
  base: ServiceConfigOption[],
  addons: (ServiceRow & { configoptions?: ServiceConfigOption[] })[],
  entityField: 'name' | 'service_name',
): ConversantRow[] {
  const entityName = entityField === 'service_name' ? detail.service_name : (detail.name ?? null);
  const rows: ConversantRow[] = base.map((c) => ({
    key: `base-${c.service_configoption_id}`,
    entityName,
    metricName: c.name,
    commitment_value: c.commitment_value,
    commitment_time: c.commitment_time,
    commitment_prepaid: c.commitment_prepaid,
    commitment_price_per_unit: c.commitment_price_per_unit,
    commitment_price_total: c.commitment_price_total,
    commitment_startdate: c.commitment_startdate,
    commitment_enddate: c.commitment_enddate,
    employee_names: detail.employee_names,
    service_start_date: detail.service_start_date,
    service_stopped_date: detail.service_stopped_date,
  }));
  for (const a of addons) {
    rows.push({
      key: `addon-${a.service_id}`,
      entityName: a.name,
      metricName: `[ADDON] - ${a.service_name}`,
      isAddon: true,
      commitment_value: a.configoptions?.[0]?.commitment_value ?? 0,
      commitment_time: 1,
      commitment_price_total: a.service_price_register,
      employee_names: detail.employee_names,
      service_start_date: a.service_start_date,
      service_stopped_date: a.service_stopped_date,
    });
  }
  return rows;
}

/**
 * Shared Conversant Information config table — one row per configoption plus each
 * addon service, parameterised by template (metric label + entity field). Mirrors
 * legacy Transcoding/Recording/Transmuxing/StorageLFD/StorageOSS Information views.
 */
export function ConversantConfigTable({
  detail,
  config,
}: {
  detail: ServiceDetail;
  config: ConversantTemplateConfig;
}) {
  const id = detail.service_id;
  const currency = detail.service_currency || 'VND';
  const base = useServiceConfigoptions(id);
  const addons = useServiceAddonConfigoptions(id);

  if (base.isError) return <TabError title="Failed to load configuration" onRetry={() => base.refetch()} />;
  if (base.isLoading) return <TabLoading rows={4} />;

  const money = (v?: number) => (
    <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
      {v ? formatMoney(v, currency) : '—'}
    </span>
  );

  const baseRows = base.data?.results ?? [];
  const rows = buildRows(detail, baseRows, addons.data?.results ?? [], config.entityField);
  const hasPrice = baseRows.some((c) => (c.commitment_value ?? 0) > 0);
  const hasStopped = !!detail.service_stopped_date;

  const columns: ColumnDef<ConversantRow, unknown>[] = [
    {
      id: 'entity',
      header: 'Portal/Domain/IP',
      cell: ({ row }) => row.original.entityName || <span style={muted}>—</span>,
    },
    {
      id: 'metric',
      header: config.metricLabel,
      cell: ({ row }) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.metricName}</span>
          {row.original.isAddon ? <Badge tone="info" appearance="subtle" size="sm">addon</Badge> : null}
        </span>
      ),
    },
    {
      id: 'commitment_value',
      header: rightHeader('Committed'),
      cell: ({ row }) => commitmentCell(row.original),
    },
  ];

  if (hasPrice) {
    columns.push({
      id: 'commitment_prepaid',
      header: 'Payment',
      cell: ({ row }) =>
        (row.original.commitment_value ?? 0) > 0 ? (
          <Chip
            appearance="subtle"
            tone={row.original.commitment_prepaid ? 'success' : 'neutral'}
            size="sm"
          >
            {row.original.commitment_prepaid ? 'Prepaid' : 'Postpaid'}
          </Chip>
        ) : null,
    });
  }

  columns.push({
    id: 'unit_price',
    header: rightHeader('Unit price'),
    cell: ({ row }) => money(row.original.commitment_price_per_unit),
  });

  if (hasPrice) {
    columns.push({
      id: 'total_price',
      header: rightHeader('Total price'),
      cell: ({ row }) => money(row.original.commitment_price_total),
    });
    columns.push({
      id: 'term',
      header: rightHeader('Term'),
      cell: ({ row }) => termCell(row.original),
    });
  }

  columns.push({
    id: 'sales',
    header: 'Sales',
    cell: ({ row }) =>
      row.original.employee_names?.filter(Boolean).join(', ') || <span style={muted}>—</span>,
  });

  columns.push({
    id: 'start',
    header: rightHeader('Start date'),
    cell: ({ row }) => (
      <span style={{ display: 'block', textAlign: 'right' }}>
        {formatDate(row.original.service_start_date)}
      </span>
    ),
  });

  if (hasStopped) {
    columns.push({
      id: 'stopped',
      header: rightHeader('Stopped date'),
      cell: ({ row }) => (
        <span style={{ display: 'block', textAlign: 'right' }}>
          {formatDate(row.original.service_stopped_date)}
        </span>
      ),
    });
  }

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Configuration</CardTitle>
      </CardHeader>
      <CardBody>
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => r.key}
          emptyState={
            <EmptyState
              icon={<Boxes size={24} />}
              title="Pay as you go"
              description="No committed resources — billed on usage."
            />
          }
        />
      </CardBody>
    </Card>
  );
}
